/**
 * Snapshots.jsx (#679, renamed from Alerts in #783; v3 refresh #1138): daily
 * Snapshot configuration page, scoped to the active workspace.
 *
 * The page shows + configures snapshots for the ACTIVE workspace only (#1138).
 * Switching workspace (top bar) switches the list, the count, and the form's
 * budgets/ledgers. There is no cross-group view and no group picker.
 *
 * Time handling: backend stores `snapshotHour` as integer 0-23 UTC. The form
 * presents and accepts hours in the browser's *local* timezone - the user
 * never sees UTC. Conversion happens at submit (local -> UTC) and at display
 * (UTC -> local). Timezone is NOT persisted; whatever zone the browser is in
 * at view time wins. Half-hour-offset zones round to nearest hour.
 *
 * Default form hour: first hour starting at 08:00 local that the workspace does
 * NOT already have a snapshot configured for. Walks 8..23 then wraps 0..7.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography
} from '@mui/material';
import { IconTrash as DeleteIcon, IconPencil as EditIcon, IconCamera } from '@tabler/icons-react';
import { api } from '../services/api';
import { snapshotsAPI } from '../services/snapshotsAPI';
import { useGroup } from '../contexts/GroupContext.jsx';
import { CenteredLoader } from '../components/LoadingIndicator';
import { useToast } from '../components/ToastNotification';
import { PageHero, SectionContainer, Row } from '../components/ds';
import { makeBudgetAcronymOf, withAcronym } from '../utils/budgetGroups';

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const PREFERRED_DEFAULT_HOUR = 8;

// ----- Time helpers -------------------------------------------------------

function getLocalOffsetHours() {
  // Date.getTimezoneOffset() returns minutes WEST of UTC (e.g. PST = 480).
  return new Date().getTimezoneOffset() / 60;
}

function localHourToUTC(localHour) {
  const offsetH = getLocalOffsetHours();
  return ((Math.round(localHour + offsetH) % 24) + 24) % 24;
}

function utcHourToLocal(utcHour) {
  const offsetH = getLocalOffsetHours();
  return ((Math.round(utcHour - offsetH) % 24) + 24) % 24;
}

function localTzAbbr() {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      timeZoneName: 'short'
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value || '';
  } catch {
    return '';
  }
}

function formatLocalHour(localHour) {
  const tz = localTzAbbr();
  return `${String(localHour).padStart(2, '0')}:00${tz ? ' ' + tz : ''}`;
}

function pickDefaultLocalHour(usedLocalHoursSet) {
  for (let h = PREFERRED_DEFAULT_HOUR; h < 24; h++) {
    if (!usedLocalHoursSet.has(h)) return h;
  }
  for (let h = 0; h < PREFERRED_DEFAULT_HOUR; h++) {
    if (!usedLocalHoursSet.has(h)) return h;
  }
  return PREFERRED_DEFAULT_HOUR; // all 24 taken - fallback
}

function countItemsByType(items) {
  let budgets = 0;
  let ledgers = 0;
  for (const it of items || []) {
    if (it.type === 'budget') budgets++;
    else if (it.type === 'ledger') ledgers++;
  }
  return { budgets, ledgers };
}

const Snapshots = ({ isAuthReady = false }) => {
  const { activeGroupId, groups, loading: groupsLoading } = useGroup();
  const { showError, showSuccess } = useToast();
  // #1710: a summary email's manage link carries ?schedule=<id>, so the page
  // opens the configuration that produced the email. ?group= is consumed
  // earlier by GroupParamGate, so the workspace is already correct here.
  const [searchParams, setSearchParams] = useSearchParams();
  const scheduleParam = searchParams.get('schedule');
  const focusedScheduleRef = useRef(null);

  const activeGroupName = useMemo(
    () => (groups || []).find((g) => g.groupId === activeGroupId)?.name || '',
    [groups, activeGroupId]
  );

  const [allAlerts, setAllAlerts] = useState(null);
  const [loadingAll, setLoadingAll] = useState(true);

  // Form state. formHour is LOCAL hour. Convert to UTC only at submit.
  const [formHour, setFormHour] = useState(PREFERRED_DEFAULT_HOUR);
  const [editingId, setEditingId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, alert: null });
  const [submitting, setSubmitting] = useState(false);
  const [hourTouched, setHourTouched] = useState(false);

  const [budgets, setBudgets] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  // #1746: which block leads the email body. 'budget' matches the backend default.
  const [formFirstSection, setFormFirstSection] = useState('budget');
  const [loadingItems, setLoadingItems] = useState(false);

  // Budgets/ledgers for the active workspace (the only group this page acts on).
  useEffect(() => {
    if (!activeGroupId) return;
    let cancelled = false;
    setLoadingItems(true);
    Promise.all([
      api.get(activeGroupId, '/budgets').catch(() => ({ budgets: [] })),
      api.get(activeGroupId, '/ledgers').catch(() => ({ ledgers: [] }))
    ]).then(([b, l]) => {
      if (cancelled) return;
      // #2116: each option carries its category acronym.
      const budgetAcronymOf = makeBudgetAcronymOf(b?.budgetCategories);
      setBudgets((b?.budgets || []).map((budget) => ({ ...budget, categoryAcronym: budgetAcronymOf(budget) })));
      setLedgers(l?.ledgers || []);
    }).finally(() => { if (!cancelled) setLoadingItems(false); });
    return () => { cancelled = true; };
  }, [activeGroupId]);

  const loadAll = useCallback(async () => {
    setLoadingAll(true);
    try {
      const res = await snapshotsAPI.listMine();
      setAllAlerts(res?.snapshotSchedules || []);
    } catch (err) {
      showError(err.body?.error || err.message || 'Failed to load email summaries');
      setAllAlerts([]);
    } finally {
      setLoadingAll(false);
    }
  }, [showError]);

  useEffect(() => { if (isAuthReady) loadAll(); }, [isAuthReady, loadAll]);

  // Scope to the active workspace: the list, count, and hour-collision check
  // only consider this workspace's schedules.
  const alerts = useMemo(
    () => (allAlerts || []).filter((a) => a.groupId === activeGroupId),
    [allAlerts, activeGroupId]
  );

  const usedLocalHours = useMemo(() => {
    const set = new Set();
    for (const a of alerts) set.add(utcHourToLocal(a.snapshotHour));
    return set;
  }, [alerts]);

  // Seed formHour to the next free local hour (from 08:00) unless editing or
  // the user already picked one.
  useEffect(() => {
    if (editingId || hourTouched || allAlerts === null) return;
    setFormHour(pickDefaultLocalHour(usedLocalHours));
  }, [editingId, hourTouched, allAlerts, usedLocalHours]);

  const itemSelectValue = selectedItems.map((it) => `${it.type}#${it.id}`);

  const onItemSelectChange = (e) => {
    const values = e.target.value;
    const parsed = (Array.isArray(values) ? values : [values]).map((v) => {
      const [type, ...rest] = v.split('#');
      return { type, id: rest.join('#') };
    });
    setSelectedItems(parsed);
  };

  const resetForm = () => {
    setEditingId(null);
    setHourTouched(false);
    setFormHour(pickDefaultLocalHour(usedLocalHours));
    setSelectedItems([]);
    setFormFirstSection('budget');
  };

  const editAlert = (alert) => {
    setEditingId(alert.snapshotScheduleId);
    setHourTouched(true);
    setFormHour(utcHourToLocal(alert.snapshotHour));
    setSelectedItems((alert.items || []).map((it) => ({ type: it.type, id: it.id })));
    setFormFirstSection(alert.firstSection || 'budget');
  };

  // #1710: open the schedule named by ?schedule=, then strip the parameter.
  // Matching on groupId too keeps a foreign workspace's schedule from opening
  // against this workspace's budget/ledger options when the group switch was
  // skipped (unknown or inaccessible ?group=). A miss degrades to the plain
  // page. Consumed once, so the loadAll() after a save cannot reopen the form.
  useEffect(() => {
    if (!scheduleParam) return;
    if (allAlerts === null) return;
    if (focusedScheduleRef.current === scheduleParam) return;
    focusedScheduleRef.current = scheduleParam;

    const match = allAlerts.find(
      (a) => a.snapshotScheduleId === scheduleParam && a.groupId === activeGroupId
    );
    if (match) editAlert(match);

    const next = new URLSearchParams(searchParams);
    next.delete('schedule');
    setSearchParams(next, { replace: true });
    // editAlert is a stable local closure over setState calls only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleParam, allAlerts, activeGroupId, searchParams, setSearchParams]);

  const handleSubmit = async () => {
    if (!activeGroupId) { showError('No active workspace'); return; }
    if (selectedItems.length === 0) { showError('Pick at least one budget or ledger'); return; }
    setSubmitting(true);
    try {
      const body = {
        snapshotHour: localHourToUTC(formHour),
        items: selectedItems,
        firstSection: formFirstSection
      };
      if (editingId) {
        await snapshotsAPI.update(activeGroupId, editingId, body);
        showSuccess('Email summary updated');
      } else {
        await snapshotsAPI.create(activeGroupId, body);
        showSuccess('Email summary created');
      }
      resetForm();
      await loadAll();
    } catch (err) {
      showError(err.body?.error || err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (alert) => {
    setDeleteDialog({ open: true, alert });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, alert: null });
  };

  const handleDeleteConfirm = async () => {
    const alert = deleteDialog.alert;
    setDeleteDialog({ open: false, alert: null });
    if (!alert) return;
    try {
      await snapshotsAPI.remove(alert.groupId, alert.snapshotScheduleId);
      showSuccess('Email summary deleted');
      if (editingId === alert.snapshotScheduleId) resetForm();
      await loadAll();
    } catch (err) {
      showError(err.body?.error || err.message || 'Delete failed');
    }
  };

  if (!isAuthReady || groupsLoading) return <CenteredLoader />;

  const total = alerts.length;

  return (
    <Box sx={{ pb: 8 }}>
      {/* v4 board (#1377): title names the workspace and carries the count in
          brackets (#1895). The count used to sit in a 14px right-aligned aux
          block that read as decoration and went unnoticed. No workspace name
          means the bare page name rather than filler text. */}
      <PageHero
        label={
          activeGroupName
            ? `Email summaries for Workspace: ${activeGroupName} [${total}]`
            : `Email summaries [${total}]`
        }
      />

      <Box sx={{ mt: { xs: '12px', md: '14px' } }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Summary emails come from <strong>noreply@budgetrecon.com</strong>. The first
          few may land in your Spam or Promotions folder - mark as Not Spam / move
          to Inbox so future ones arrive normally.
        </Alert>

        {/* v4 board: wide = two equal columns; mobile stacks Scheduled first
            (previously the configured list was hidden below md). */}
        <Box
          sx={{
            display: 'grid',
            gap: '16px',
            alignItems: 'start',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
          }}
        >
          <Box sx={{ minWidth: 0, order: { xs: 2, md: 1 } }}>
            <SectionContainer title={editingId ? 'Edit email summary' : 'New email summary'} category="budgets">
              <Stack spacing={2} sx={{ pt: 1 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="snap-hour-label">Send at (your local time)</InputLabel>
                    <Select
                      labelId="snap-hour-label"
                      label="Send at (your local time)"
                      value={formHour}
                      onChange={(e) => { setHourTouched(true); setFormHour(Number(e.target.value)); }}
                    >
                      {HOURS.map((h) => <MenuItem key={h} value={h}>{formatLocalHour(h)}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Stack>

                <FormControl size="small" fullWidth>
                  <InputLabel id="snap-items-label">Include</InputLabel>
                  <Select
                    labelId="snap-items-label"
                    label="Include"
                    multiple
                    value={itemSelectValue}
                    onChange={onItemSelectChange}
                    renderValue={(selected) => `${selected.length} item${selected.length === 1 ? '' : 's'}`}
                    disabled={loadingItems}
                  >
                    {budgets.length > 0 && <ListSubheader>Budgets</ListSubheader>}
                    {budgets.map((b) => (
                      <MenuItem key={`b-${b.budgetId}`} value={`budget#${b.budgetId}`}>
                        {withAcronym(b.name, b.categoryAcronym)}
                      </MenuItem>
                    ))}
                    {ledgers.length > 0 && <ListSubheader>Ledgers</ListSubheader>}
                    {ledgers.map((l) => (
                      <MenuItem key={`l-${l.ledgerId}`} value={`ledger#${l.ledgerId}`}>{l.name}</MenuItem>
                    ))}
                    {budgets.length === 0 && ledgers.length === 0 && (
                      <MenuItem disabled>No budgets or ledgers in this workspace</MenuItem>
                    )}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel id="snap-order-label">Section order</InputLabel>
                  <Select
                    labelId="snap-order-label"
                    label="Section order"
                    value={formFirstSection}
                    onChange={(e) => setFormFirstSection(e.target.value)}
                  >
                    <MenuItem value="budget">Budgets first</MenuItem>
                    <MenuItem value="ledger">Ledgers first</MenuItem>
                  </Select>
                </FormControl>

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting || selectedItems.length === 0}
                  >
                    {editingId ? 'Update email summary' : 'Create email summary'}
                  </Button>
                  {editingId && (
                    <Button onClick={resetForm} disabled={submitting}>Cancel edit</Button>
                  )}
                </Stack>
              </Stack>
            </SectionContainer>
          </Box>

          {/* Scheduled list - visible on every size (v4). */}
          <Box
            data-testid="snapshots-side-panel"
            sx={{ minWidth: 0, order: { xs: 1, md: 2 } }}
          >
            <SectionContainer title="Configured email summaries" count={total} category="interactive">
              {loadingAll ? (
                <Typography sx={{ color: 'text.secondary', fontSize: 14, py: 1 }}>Loading...</Typography>
              ) : total === 0 ? (
                <Typography sx={{ color: 'text.secondary', fontSize: 14, py: 1 }}>None yet</Typography>
              ) : (
                alerts.map((a, idx) => {
                  const { budgets: bCount, ledgers: lCount } = countItemsByType(a.items);
                  const localHour = utcHourToLocal(a.snapshotHour);
                  return (
                    <Row
                      key={a.snapshotScheduleId}
                      icon={<IconCamera />}
                      category="interactive"
                      name={formatLocalHour(localHour)}
                      meta={`${bCount} ${bCount === 1 ? 'budget' : 'budgets'} · ${lCount} ${lCount === 1 ? 'ledger' : 'ledgers'}`}
                      actions={
                        <Box sx={{ display: 'flex', gap: 0.25 }}>
                          <IconButton
                            size="small"
                            onClick={() => editAlert(a)}
                            aria-label="edit email summary"
                            sx={(t) => ({ color: t.palette.text.secondary, padding: '2px' })}
                          >
                            <EditIcon size={15} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(a)}
                            aria-label="delete email summary"
                            sx={(t) => ({ color: t.palette.text.secondary, padding: '2px', '&:hover': { color: t.palette.error.main } })}
                          >
                            <DeleteIcon size={15} />
                          </IconButton>
                        </Box>
                      }
                      last={idx === total - 1}
                    />
                  );
                })
              )}
            </SectionContainer>
          </Box>
        </Box>
      </Box>

      {/* Delete Email Summary Confirmation Dialog (#1452) */}
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Delete email summary?</DialogTitle>
        <DialogContent>
          <Typography>
            {`Delete the email summary at ${deleteDialog.alert ? formatLocalHour(utcHourToLocal(deleteDialog.alert.snapshotHour)) : ''}?`}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Snapshots;
